/* global window */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

import { useGqlContext } from '@search/gql-client/src/gqlContext';

import { ProfessionalSearchInput } from '../../../domain/offer/search/page/SearchOffersQuery/__generated__/SearchOffersQuery.graphql';
import type { LastSearches } from '../../../domain/offer/search/page/SearchOffersQuery';
import type {
    TrackSearchMutation$data, TrackSearchMutation$variables
} from './__generated__/TrackSearchMutation.graphql';

import { TrackSearchMutation } from './TrackSearchMutation';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useLastSearches = ({
    filtersParams: filters,
    lastSearches: initialLastSearches,
    currentUrl,
    isMobile,
    isShowedInSegment,
    isFiltersChanged,
    isNeedlessSave: initialIsNeedlessSave,
}: {
    filtersParams: ProfessionalSearchInput;
    lastSearches?: LastSearches;
    currentUrl: string;
    isMobile?: boolean;

    isShowedInSegment: boolean;
    isNeedlessSave: boolean;
    isFiltersChanged: boolean;
}) => {
    const gql = useGqlContext();

    const { isSearchAlreadySaved, lastSearches } = useMemo(() => {
        const isAlreadySaved = (initialLastSearches ?? [])[0]?.searchUrl === currentUrl;

        if (! isShowedInSegment || isAlreadySaved) {
            return {
                lastSearches: [],
                isSearchAlreadySaved: isAlreadySaved
            };
        }

        type T = NonNullable<typeof initialLastSearches>[0];
        // Чтобы не показать на странице такой же поиск
        const filteredLastSearches = (initialLastSearches ?? [])
            .filter((search: T): search is NonNullable<T> => {
                return search?.searchUrl ? search.searchUrl !== currentUrl : false;
            })
            .map((search) => ({
                ...search,
                searchText: isMobile && (search?.searchText.length ?? 0) > 77 ?
                    search?.searchText.slice(0, 77) + '...' : search?.searchText,
            }));

        return {
            lastSearches: filteredLastSearches,
            isSearchAlreadySaved: isAlreadySaved
        };
    }, [ initialLastSearches, isShowedInSegment, currentUrl, isMobile ]);

    const isNeedlessSave = initialIsNeedlessSave || ! isShowedInSegment;
    // for unsubscribe in effects
    const [ isLastSearchSaved, setLastSearchSaved ] = useState(isSearchAlreadySaved);
    const isNoShowing = isFiltersChanged || isLastSearchSaved;
    // for instantly react in track callback
    const isLastSearchSavedRef = useRef(false);
    const isLastSearchSaving = useRef(false);

    useEffect(() => {
        // if initial search is always saved, but filters have been changed - unlock subscribe effects
        if (isFiltersChanged) setLastSearchSaved(false);
    }, [ isFiltersChanged ]);

    const saveLastSearch = useCallback(() => {
        if (isNeedlessSave) {
            isLastSearchSaving.current = false;
        } else {
            gql.client<TrackSearchMutation$variables, TrackSearchMutation$data>(TrackSearchMutation, { filters })
                .then(res => {
                    if (res.data?.trackSearch?.success) {
                        isLastSearchSavedRef.current = true;
                        setLastSearchSaved(true);
                    }
                })
                .finally(() => isLastSearchSaving.current = false);
        }
    }, [ gql, filters, isNeedlessSave ]);

    // кажется, излишне, нужно обязательно проскроллить, чтобы сохранять.
    // useEffect(() => {
    // if (! (isNeedlessSave || isLastSearchSaved)) window.addEventListener('beforeunload', saveLastSearch);
    // if (isNeedlessSave || isLastSearchSaved) {
    //     window.removeEventListener('beforeunload', saveLastSearch);
    // }

    // return () => window.removeEventListener('beforeunload', saveLastSearch);
    // }, [ isNeedlessSave, isLastSearchSaved, saveLastSearch ]);

    const handleScroll = useCallback(() => {
        // отступ от нижней границы первого экрана
        const scrollPosition = isMobile ? 1600 : 700;

        if (window.scrollY > scrollPosition && ! isLastSearchSaving.current && ! isLastSearchSavedRef.current) {
            isLastSearchSaving.current = true;
            saveLastSearch();
        }
    }, [ saveLastSearch, isMobile ]);

    useEffect(() => {
        const throttledHandleScroll = throttle(handleScroll, 300);

        if (! (isNeedlessSave || isLastSearchSaved)) window.addEventListener('scroll', throttledHandleScroll);
        if (isNeedlessSave || isLastSearchSaved) window.removeEventListener('scroll', throttledHandleScroll);
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
        };
    }, [ isLastSearchSaved, isNeedlessSave, handleScroll ]);

    return {
        lastSearches: isNoShowing ? [] : lastSearches
    };
};
